import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { type DragEvent, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Waypoint } from 'react-waypoint';
import { useSingleEmailComposer, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../../../models/NameUtils';
import { FileSendLinkPlaceholder } from '../../../../../../models/Token';
import { createRawRichTextContentStateWithHtmlStringValue } from '../../../../../../extViewmodels/sdk/Utils';
import { EmailMessageViewModel } from '../../../../../../viewmodels/AppViewModels';
import { escapeHtml } from '../../../../../../queries/Admin/Calendar/textToHtmlUtils';
import { isFilesFeatureEnabled } from '../../../../../../models/utils/accountUtils';
import {
	invalidateContactFilesQueries,
	useContact,
	useContactFileRequestsQuery,
	useContactFilesQuery,
	useDeleteSharedFileMutation,
	useFileRequestSurveyQueries,
	useUploadContactFilesMutation,
	useUserQueries,
} from '../../../../../../queries';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Placeholder } from '../../../../../components/Placeholder';
import { FileTextIcon } from '../../../../../components/svgs/icons/FileTextIcon';
import { Button } from '../../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../components-v2/Dialog';
import { Label } from '../../../../../components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components-v2/Select';
import { DeleteFileConfirmModal } from './DeleteFileConfirmModal';
import { FileRequestCard } from './FileRequestCard';
import { FilesSection, SECTION_ROW_LIMIT } from './FilesSection';
import { FileRequestFilter, filterFileRequestGroups, groupContactFiles, toContactFileRequest } from './fileRequests';
import { RequestFilesDialog } from './RequestFilesDialog';
import { SendFilesDialog } from './SendFilesDialog';
import { SharedFilesTable } from './SharedFilesTable';
import { styleSheet } from './styles';

const isHttpsUrl = (value: string) => {
	try {
		return new URL(value).protocol === 'https:';
	} catch {
		return false;
	}
};

const MAX_FILE_SIZE_BYTES = 20 * 1_000_000;

const FILE_REQUEST_LINK_TEXT = 'Securely Upload Files';

function filterByMaxSize(files: File[]): { accepted: File[]; rejected: File[] } {
	const accepted: File[] = [];
	const rejected: File[] = [];
	for (const file of files) {
		if (file.size <= MAX_FILE_SIZE_BYTES) {
			accepted.push(file);
		} else {
			rejected.push(file);
		}
	}
	return { accepted, rejected };
}

function filterByTotalSize(newFiles: File[], currentBytes: number): { accepted: File[]; rejected: File[] } {
	const accepted: File[] = [];
	const rejected: File[] = [];
	let running = currentBytes;
	for (const file of newFiles) {
		if (running + file.size <= MAX_FILE_SIZE_BYTES) {
			accepted.push(file);
			running += file.size;
		} else {
			rejected.push(file);
		}
	}
	return { accepted, rejected };
}

const REQUEST_FILTER_OPTIONS = [
	{ value: FileRequestFilter.Open, label: 'Open requests' },
	{ value: FileRequestFilter.Waiting, label: 'Waiting on upload' },
];

const REQUEST_FILTER_ID = 'contact-files-request-filter';

const OTHER_FILES_HINT =
	'Files on this contact that were not uploaded to a file request or sent to them, such as files you uploaded, email attachments, text messages, survey answers and notes.';

function limitRows<T>(rows: T[], isExpanded: boolean) {
	return isExpanded ? rows : rows.slice(0, SECTION_ROW_LIMIT);
}

type IProps = {
	contactId?: string;
};

export const ContactFiles = ({ contactId }: IProps) => {
	const userSession = useUserSession();
	const singleEmailComposer = useSingleEmailComposer();
	const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
	const [modalStep, setModalStep] = useState<null | 'upload' | 'confirm'>(null);
	const [filesToConfirm, setFilesToConfirm] = useState<{ name: string }[]>([]);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [filesToSend, setFilesToSend] = useState<File[]>([]);
	const [dragActive, setDragActive] = useState(false);
	const [uploadMode, setUploadMode] = useState<'upload' | 'send'>('upload');
	const [fileToDelete, setFileToDelete] = useState<Api.ISharedFile | null>(null);

	const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);

	// The link is minted for this contact, so the composer opens addressed to them alone.
	const handleFileRequestCreated = (link: string) => {
		setIsRequestDialogOpen(false);
		if (!isHttpsUrl(link)) {
			toast.error('Failed to create the file request link');
			return;
		}
		const contactVm = new Api.ContactViewModel(userSession, { id: contactId } as Api.IContact);
		const emailMessage = new EmailMessageViewModel<File>(userSession, true);
		emailMessage.contactsToAdd.add(contactVm);
		emailMessage.options.sendSingleEmail = true;
		emailMessage.content = createRawRichTextContentStateWithHtmlStringValue(
			`<p><a href="${escapeHtml(link)}">${FILE_REQUEST_LINK_TEXT}</a></p>`
		);
		singleEmailComposer.showWithEmailMessage(emailMessage, [contactVm]);
	};

	const contactQuery = useContact({ contactId: contactId ?? '', enabled: !!contactId });

	const filesQuery = useContactFilesQuery({
		contactId: contactId ?? '',
		enabled: !!contactId,
	});

	const { fetchNextPage, hasNextPage, isFetchingNextPage } = filesQuery;

	const deleteSharedFileMutation = useDeleteSharedFileMutation({
		onSuccess: () => {
			toast.success('File deleted');
			setFileToDelete(null);
		},
		onError: () => {
			toast.error('Failed to delete file');
		},
	});

	const files = useMemo(
		() => filesQuery.data?.pages.flatMap((page: Api.IPagedCollection<Api.ISharedFile>) => page.values) ?? [],
		[filesQuery.data]
	);

	const handleDownload = async (file: Api.ISharedFile) => {
		setDownloadingFileId(file.id);
		try {
			const attachment = await userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `attachment/${file.fileAttachment.id}` }),
				'GET'
			);
			if (attachment.url) {
				const link = document.createElement('a');
				link.href = attachment.url;
				link.download = attachment.fileName;
				link.click();
				link.remove();
			}
		} catch {
			toast.error('Failed to download file');
		} finally {
			setDownloadingFileId(null);
		}
	};

	const uploadContactFilesMutation = useUploadContactFilesMutation({
		onError: () => {
			toast.error('Failed to upload files');
		},
	});

	const handleUploadFilesClick = () => {
		setUploadMode('upload');
		setModalStep('upload');
	};

	const [requestFilter, setRequestFilter] = useState<FileRequestFilter>(FileRequestFilter.Open);
	const [expandedSections, setExpandedSections] = useState({ requests: false, other: false, sent: false });

	const fileRequestsQuery = useContactFileRequestsQuery({
		contactId: contactId ?? '',
		enabled: !!contactId,
	});
	const responses = fileRequestsQuery.data ?? [];
	const surveyIds = [...new Set(responses.map(response => response.surveyId).filter(Boolean))];
	const surveyQueries = useFileRequestSurveyQueries({ ids: surveyIds });
	const surveysById = new Map(
		surveyQueries.flatMap((query, i) => (query.data ? [[surveyIds[i], query.data] as const] : []))
	);

	const requests = responses.map(response => toContactFileRequest(response, surveysById.get(response.surveyId)));
	const { groups, sentFiles, otherFiles } = groupContactFiles(requests, files, new Date());
	const visibleGroups = filterFileRequestGroups(groups, requestFilter);
	const shownGroups = limitRows(visibleGroups, expandedSections.requests);
	const shownOtherFiles = limitRows(otherFiles, expandedSections.other);
	const shownSentFiles = limitRows(sentFiles, expandedSections.sent);

	const setSectionExpanded = (section: keyof typeof expandedSections) => (isExpanded: boolean) =>
		setExpandedSections(prev => ({ ...prev, [section]: isExpanded }));

	const senderIds = [...new Set(requests.map(request => request.senderUserId).filter(Boolean))];
	const senderQueries = useUserQueries({ ids: senderIds });
	const senderNames = new Map(
		senderQueries.flatMap((query, i) => (query.data ? [[senderIds[i], getDisplayName(query.data)] as const] : []))
	);

	const handleSendFilesClick = () => {
		setUploadMode('send');
		setModalStep('upload');
	};

	const handleModalClose = () => {
		setModalStep(null);
		setUploadedFiles([]);
		setFilesToSend([]);
		setDragActive(false);
	};

	const handleUploadNext = () => {
		if (uploadMode === 'send') {
			setFilesToConfirm(uploadedFiles.map(f => ({ name: f.name })));
			setFilesToSend(uploadedFiles);
			setUploadedFiles([]);
			setModalStep('confirm');
		} else {
			if (!contactId) {
				return;
			}
			uploadContactFilesMutation.mutate(
				{ contactId, files: uploadedFiles },
				{
					onSuccess: () => {
						toast.success('Files uploaded');
						handleModalClose();
					},
				}
			);
		}
	};

	const addFiles = (picked: File[]) => {
		const { accepted: sizeOk, rejected: tooLarge } = filterByMaxSize(picked);
		if (tooLarge.length > 0) {
			toast.error(
				`${tooLarge.length} file${tooLarge.length !== 1 ? 's' : ''} removed — files must be no larger than 20 MB`
			);
		}
		const currentBytes = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
		const { accepted, rejected: overTotal } = filterByTotalSize(sizeOk, currentBytes);
		if (overTotal.length > 0) {
			toast.error(
				`${overTotal.length} file${overTotal.length !== 1 ? 's' : ''} removed — total upload size cannot exceed 20 MB`
			);
		}
		if (accepted.length > 0) {
			setUploadedFiles(prev => [...prev, ...accepted]);
		}
	};

	const handleDrag = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		addFiles(Array.from(e.dataTransfer.files ?? []));
	};

	// The API mints the link when the email sends, so the files land on the email's note, which is what records
	// them as sent to the contact. A link minted up front never reaches the note.
	const handleSendFilesContinue = (sharedLinkCreationOptions: Api.ISharedLinkCreationOptions) => {
		const pickedFiles = filesToSend;
		setModalStep(null);
		setFilesToSend([]);
		const contactVm = new Api.ContactViewModel(userSession, { id: contactId } as Api.IContact);
		const emailMessage = new EmailMessageViewModel<File>(userSession, true);
		emailMessage.contactsToAdd.add(contactVm);
		emailMessage.options.sendSingleEmail = true;
		emailMessage.options.saveAsNote = true;
		emailMessage.sendAttachmentsAsLink = true;
		emailMessage.sharedLinkCreationOptions = sharedLinkCreationOptions;
		emailMessage.setAttachments(
			new Api.AttachmentsToBeUploadedViewModel<File>(
				pickedFiles,
				Api.VmUtils.getMaxAttachmentByteCountForEmailConfigurations(userSession.account?.emailProviderConfiguration)
			)
		);
		emailMessage.content = createRawRichTextContentStateWithHtmlStringValue(`<p>${FileSendLinkPlaceholder.symbol}</p>`);
		singleEmailComposer.showWithEmailMessage(emailMessage, [contactVm], didSend => {
			if (didSend && contactId) {
				invalidateContactFilesQueries({ contactId });
			}
		});
	};

	const filesGroupPermissions = userSession.user?.userGroupPermissions?.featurePermissions?.files;
	const canSendFiles =
		isFilesFeatureEnabled(userSession.account) &&
		!!userSession.account?.features?.files?.canSendFileLinks &&
		!!filesGroupPermissions?.sendDownloadLink;
	const canRequestFiles = isFilesFeatureEnabled(userSession.account) && !!filesGroupPermissions?.requestFile;
	const canDeleteFiles = isFilesFeatureEnabled(userSession.account) && !!filesGroupPermissions?.delete;
	const canUploadFiles = isFilesFeatureEnabled(userSession.account) && !!filesGroupPermissions?.write;

	const isClientPortalEnabled = !!userSession.account?.features?.files?.clientPortalEnabled;
	const isLoading = filesQuery.isPending || fileRequestsQuery.isPending || surveyQueries.some(query => query.isPending);
	const contact = contactQuery.data;
	const contactName = contact ? getDisplayName(contact) : '';
	const showRequests = groups.length > 0;
	const showOtherFiles = otherFiles.length > 0;
	const showSentFiles = sentFiles.length > 0;
	const showActions = canUploadFiles || canSendFiles || canRequestFiles;

	if (!contactId) {
		return;
	}

	const tableProps = {
		contact,
		canDelete: canDeleteFiles,
		downloadingFileId,
		onDownload: handleDownload,
		onDelete: setFileToDelete,
	};

	return (
		<div className={css(styleSheet.container)}>
			{showActions && (
				<div className={css(styleSheet.actions)}>
					{canUploadFiles && (
						<Button variant='secondary' onClick={handleUploadFilesClick}>
							Upload Files
						</Button>
					)}
					{canSendFiles && (
						<Button variant='secondary' onClick={handleSendFilesClick}>
							Send Files
						</Button>
					)}
					{canRequestFiles && (
						<Button variant='secondary' onClick={() => setIsRequestDialogOpen(true)}>
							Request Files
						</Button>
					)}
				</div>
			)}
			{isClientPortalEnabled && (
				<div className={css(styleSheet.portalBanner)}>
					<span>
						<strong>Client portal is on.</strong> {contact?.firstName || 'This contact'} sees every request below and
						everything you send them.
					</span>
				</div>
			)}
			<div className={css(styleSheet.content)}>
				<Placeholder
					isLoading={isLoading}
					isEmpty={!isLoading && !showRequests && !showOtherFiles && !showSentFiles}
					message='No files have been shared with this contact yet.'
				/>
				{!isLoading && showRequests && (
					<FilesSection
						title='Requests'
						count={visibleGroups.length}
						itemNoun='requests'
						isExpanded={expandedSections.requests}
						onExpandedChange={setSectionExpanded('requests')}
						actions={
							<div className={css(styleSheet.filterRow)}>
								<Label htmlFor={REQUEST_FILTER_ID}>Show</Label>
								<Select
									items={REQUEST_FILTER_OPTIONS}
									value={requestFilter}
									onValueChange={value => setRequestFilter(value as FileRequestFilter)}
								>
									<SelectTrigger id={REQUEST_FILTER_ID} styles={[styleSheet.filterTrigger]}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{REQUEST_FILTER_OPTIONS.map(option => (
											<SelectItem key={option.value} value={option.value} label={option.label}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						}
					>
						{visibleGroups.length === 0 ? (
							<p className={css(styleSheet.emptyMessage)}>No requests match.</p>
						) : (
							<div className={css(styleSheet.requestList)}>
								{shownGroups.map(group => (
									<FileRequestCard
										key={group.request.responseId}
										group={group}
										senderName={senderNames.get(group.request.senderUserId)}
									>
										<SharedFilesTable files={group.files} variant='request' {...tableProps} />
									</FileRequestCard>
								))}
							</div>
						)}
					</FilesSection>
				)}
				{!isLoading && showSentFiles && (
					<FilesSection
						title={`Sent to ${contactName || 'this contact'}`}
						count={sentFiles.length}
						itemNoun='files'
						isExpanded={expandedSections.sent}
						onExpandedChange={setSectionExpanded('sent')}
					>
						<SharedFilesTable files={shownSentFiles} variant='sent' {...tableProps} />
					</FilesSection>
				)}
				{!isLoading && showOtherFiles && (
					<FilesSection
						title={groups.length > 0 ? 'Other files' : 'Received files'}
						titleHint={OTHER_FILES_HINT}
						count={otherFiles.length}
						itemNoun='files'
						isExpanded={expandedSections.other}
						onExpandedChange={setSectionExpanded('other')}
					>
						<SharedFilesTable files={shownOtherFiles} variant='received' {...tableProps} />
					</FilesSection>
				)}
				{hasNextPage && !isFetchingNextPage && <Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} />}
				{isFetchingNextPage && <LoadingSpinner type='extra-tiny' />}
			</div>
			{contactId ? (
				<SendFilesDialog
					fileNames={filesToConfirm.map(f => f.name)}
					onClose={() => setModalStep(null)}
					onContinue={handleSendFilesContinue}
					open={modalStep === 'confirm'}
				/>
			) : null}
			<Dialog
				open={modalStep === 'upload'}
				onOpenChange={open => {
					if (!open && !uploadContactFilesMutation.isPending) handleModalClose();
				}}
			>
				<DialogPopup showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>{uploadMode === 'upload' ? 'Upload Files' : 'Send Files'}</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div
							className={css(styleSheet.dropzone, dragActive && styleSheet.dropzoneActive)}
							onDragEnter={handleDrag}
							onDragLeave={handleDrag}
							onDragOver={handleDrag}
							onDrop={handleDrop}
							onClick={() => fileInputRef.current?.click()}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									fileInputRef.current?.click();
								}
							}}
							role='button'
							tabIndex={0}
							aria-label='Drop files here or click to browse'
						>
							<div className={css(styleSheet.dropzoneContent)}>
								<p className={css(styleSheet.dropzoneTitle)}>Drop files here or click to browse</p>
								<p className={css(styleSheet.dropzoneDescription)}>
									{uploadMode === 'upload'
										? 'Select one or more files to upload to this contact'
										: 'Select one or more files to send'}
								</p>
							</div>
							<input
								ref={fileInputRef}
								type='file'
								multiple
								className={css(styleSheet.hiddenInput)}
								onChange={e => {
									addFiles(Array.from(e.target.files ?? []));
									if (fileInputRef.current) fileInputRef.current.value = '';
								}}
							/>
						</div>
						{uploadedFiles.length > 0 && (
							<div className={css(styleSheet.uploadedFilesList)}>
								<span className={css(styleSheet.filesCount)}>
									{uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} selected
								</span>
								<div className={css(styleSheet.fileRows)}>
									{uploadedFiles.map((f, i) => (
										<div key={`${f.name}-${i}`} className={css(styleSheet.fileRow)}>
											<FileTextIcon width={16} height={16} className={css(styleSheet.fileIcon)} />
											<span className={css(styleSheet.fileRowName)}>{f.name}</span>
											<Button
												variant='link'
												onClick={() => setUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
												aria-label={`Remove ${f.name}`}
											>
												×
											</Button>
										</div>
									))}
								</div>
							</div>
						)}
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' disabled={uploadContactFilesMutation.isPending} onClick={handleModalClose}>
							Cancel
						</Button>
						<Button
							disabled={uploadedFiles.length === 0}
							isLoading={uploadContactFilesMutation.isPending}
							onClick={handleUploadNext}
						>
							{uploadMode === 'upload' ? 'Upload' : 'Next'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			{contactId ? (
				<RequestFilesDialog
					contactId={contactId}
					onClose={() => setIsRequestDialogOpen(false)}
					onCreated={handleFileRequestCreated}
					open={isRequestDialogOpen}
				/>
			) : null}
			<DeleteFileConfirmModal
				file={fileToDelete}
				isDeleting={deleteSharedFileMutation.isPending}
				onClose={() => setFileToDelete(null)}
				onConfirm={() => {
					if (!fileToDelete) return;
					deleteSharedFileMutation.mutate({
						id: fileToDelete.id,
						deleteSourceFile: true,
						contactId: contactId ?? '',
					});
				}}
			/>
		</div>
	);
};
